import React, {useCallback, useMemo} from 'react';

import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {SemanticLegendSettings, YAxisValues} from '../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from './SemanticLegendDefaults';

type YValueSelectOption = {
  value: string;
  label: string;
};

export type SemanticLegendYValueProps = {
  yValue: YAxisValues;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
};

export const SemanticLegendYValue = ({
  updateSetting,
  yValue,
}: SemanticLegendYValueProps) => {
  const yValueOptions = getYValueOptions();

  const yValueOption = useMemo(() => {
    if (yValue === null) {
      return {
        value: DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue,
        label: getYValueDisplayName(
          DEFAULT_SEMANTIC_LEGEND_SETTINGS.yAxisValue
        ),
      };
    }
    return {
      value: yValue,
      label: getYValueDisplayName(yValue),
    };
  }, [yValue]);

  const onYValueChange = useCallback(
    (selected: YValueSelectOption | null) => {
      if (selected != null) {
        updateSetting({
          yAxisValue: selected.value as YAxisValues,
          shouldRecalculateBuckets: true, // we want to recalculate the buckets bounds when the y value changes
        });
      }
    },
    [updateSetting]
  );

  return (
    <div className={'flex flex-col'} data-test="y-value-setting">
      <div className="flex w-full">
        <SettingLabel text="Y value" className="ml-14 w-[87px]" />
        <div className="item-center flex w-full flex-row gap-8">
          <SettingSelect
            className="grow"
            options={yValueOptions}
            value={yValueOption}
            onChange={onYValueChange}
          />
        </div>
      </div>
    </div>
  );
};

const yValueOptionsMeta: Record<string, {displayName: string}> = {
  ['min']: {
    displayName: 'Min',
  },
  ['max']: {
    displayName: 'Max',
  },
  ['last']: {
    displayName: 'Latest',
  },
};

const getYValueDisplayName = (yValue: string) => {
  return yValueOptionsMeta[yValue]?.displayName;
};

const getYValueOptions = (): YValueSelectOption[] => {
  return Object.keys(yValueOptionsMeta).map(val => ({
    label: getYValueDisplayName(val),
    value: val,
  }));
};
